/* Adds responsive navigation to the existing native zoom dialog.
 * Does not replace zoom-dialog.js or attach any drag/hover handlers to the marquee.
 */
class ProductZoomNavigation extends HTMLElement {
  #controller;
  #observer;
  #resizeObserver;
  #frame;
  #settleTimer;
  #host;
  #dialog;
  #gallery;
  #thumbnails;
  #media = [];
  #compact = matchMedia('(max-width: 749px), (max-height: 500px)');
  #index = 0;
  #requestedIndex = null;

  connectedCallback() {
    this.#frame = requestAnimationFrame(() => this.#connect());
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#settleTimer);
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
  }

  #connect() {
    this.#host = this.closest('zoom-dialog');
    this.#dialog = this.closest('dialog');
    this.#gallery = this.#dialog?.querySelector('.dialog-zoomed-gallery');
    this.#thumbnails = this.#dialog?.querySelector('.dialog-thumbnails-list');
    if (!this.#host || !this.#dialog || !this.#gallery || !this.#thumbnails) return;
    this.#media = Array.from(this.#gallery.children);
    if (!this.#media.length) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-zoom-step]');
      if (!button) return;
      event.preventDefault();
      this.#select(this.#index + Number(button.dataset.zoomStep));
    }, { signal });

    this.#dialog.addEventListener('keydown', (event) => {
      if (!this.#dialog.open || event.altKey || event.ctrlKey || event.metaKey) return;
      // Let media controls and text fields keep their own keyboard behavior.
      if (event.target.closest('video, audio, iframe, model-viewer, input, select, textarea, [contenteditable]')) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      event.stopPropagation();
      this.#select(this.#index + (event.key === 'ArrowRight' ? 1 : -1));
    }, { capture: true, signal });

    // Keep the current intended index during smooth scrolling / rapid arrow clicks.
    this.#thumbnails.addEventListener('click', (event) => {
      const button = event.target.closest('.dialog-thumbnails-list__thumbnail');
      const index = Array.from(this.#thumbnails.children).indexOf(button);
      if (index >= 0) this.#update(index);
      // The theme still handles the actual thumbnail click.
    }, { capture: true, signal });

    this.#gallery.addEventListener('scroll', () => {
      if (!this.#dialog.open) return;
      clearTimeout(this.#settleTimer);
      this.#settleTimer = setTimeout(() => this.#syncVisible(), 140);
    }, { passive: true, signal });

    // Remember the exact media clicked in any repeated marquee group.
    this.#host.closest('media-gallery')?.addEventListener('click', (event) => {
      const action = event.target.closest('[data-marquee-zoom-action]');
      const route = action?.getAttribute('on:click') || '';
      const prefix = `#${this.#host.id}/open/`;
      if (!route.startsWith(prefix)) return;
      const index = Number(route.slice(prefix.length));
      if (Number.isInteger(index) && index >= 0 && index < this.#media.length) {
        this.#requestedIndex = index;
      }
    }, { capture: true, signal });

    this.#observer = new MutationObserver(() => {
      if (!this.#dialog.open) {
        clearTimeout(this.#settleTimer);
        this.#requestedIndex = null;
        return;
      }
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => {
        if (!this.#dialog.open) return;
        if (this.#requestedIndex !== null) {
          this.#select(this.#requestedIndex, false);
          this.#requestedIndex = null;
        } else {
          this.#syncVisible();
        }
      });
    });
    this.#observer.observe(this.#dialog, { attributes: true, attributeFilter: ['open'] });

    this.#resizeObserver = new ResizeObserver(() => {
      if (!this.#dialog.open) return;
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#select(this.#requestedIndex ?? this.#index, false));
    });
    this.#resizeObserver.observe(this.#gallery);
    // Mobile images are for swiping, not dismissing. Keep desktop click-to-close.
    this.#gallery.addEventListener('click', (event) => {
      if (this.#compact.matches || !this.#dialog.open) return;
      if (event.target.closest('.product-media-container--image')) this.#host.close?.();
    }, { signal });
    this.#update(0);
    if (this.#dialog.open) this.#syncVisible();
  }

  #select(index, animate = true) {
    clearTimeout(this.#settleTimer);
    index = (index + this.#media.length) % this.#media.length;
    const behavior = animate && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant';
    this.#update(index);
    // Reuse the theme's high-resolution loading and selection events.
    if (typeof this.#host.selectThumbnail === 'function') {
      this.#host.selectThumbnail(index, { behavior });
    } else {
      this.#media[index].scrollIntoView({ behavior, block: 'nearest', inline: 'start' });
    }
  }

  #syncVisible() {
    if (!this.#dialog.open) return;
    const viewport = this.#gallery.getBoundingClientRect();
    const center = (viewport.left + viewport.right) / 2;
    let nearest = 0;
    let distance = Infinity;
    this.#media.forEach((media, index) => {
      const rect = media.getBoundingClientRect();
      const delta = Math.abs((rect.left + rect.right) / 2 - center);
      if (delta < distance) { nearest = index; distance = delta; }
    });
    this.#update(nearest);
    // Native zoom listens to dialog scrolling; the fitted stage scrolls instead.
    this.#host.handleScroll?.();
    // Keep the active thumbnail within reach after a native touch swipe.
    const thumbnail = this.#thumbnails.children[nearest];
    if (thumbnail) {
      const strip = this.#thumbnails.getBoundingClientRect();
      const rect = thumbnail.getBoundingClientRect();
      if (rect.left < strip.left || rect.right > strip.right) {
        const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.#thumbnails.scrollBy({ left: (rect.left + rect.right - strip.left - strip.right) / 2, behavior: reduced ? 'instant' : 'smooth' });
      }
    }
  }

  #update(index) {
    this.#index = index;
    Array.from(this.#thumbnails.children).forEach((button, i) => {
      button.setAttribute('aria-selected', String(i === index));
    });
    const status = this.querySelector('[data-zoom-status]');
    if (status) status.textContent = `Media ${index + 1} of ${this.#media.length}`;
  }
}

if (!customElements.get('product-zoom-navigation')) {
  customElements.define('product-zoom-navigation', ProductZoomNavigation);
}
